import type { QueryClient } from "@tanstack/react-query";
import { unwrap } from "@brad-tipper/app-frontend";

import { api } from "~/api/client";

import {
  cachePatch,
  cacheRemove,
  cacheRestore,
  cacheSnapshot,
  cacheUpsert,
  invalidateScope,
  patchRow,
  putRow,
  type EntityName,
  type List,
  type Task,
  type TaskAlert,
  type TaskCompletion,
} from "~/state/sync";

/**
 * Signed-in writes go through a mutation *default* registered here rather than
 * an inline `mutationFn`, so a write queued offline can be looked up by
 * `mutationKey` and replayed by `resumePausedMutations()`. Guest writes never
 * come here — they go straight to the guest cache (see the action hooks).
 *
 * `mutationKey` is `[entity, op]`; variables always carry `scope` (the owner
 * id) so the optimistic update and rollback can target `[entity, scope]`.
 */

type Base = { scope: string };

/** Only a mutation that is the last one in flight restores or refetches, so
 * a failure can't undo a later optimistic change and a burst of quick actions
 * refetches once. `isMutating()` counts this mutation too; mutations paused
 * offline count as pending, which just defers the refetch until they replay. */
const isLastPending = (queryClient: QueryClient) =>
  queryClient.isMutating() <= 1;

/** Entities whose mutation failed while another was still in flight: nothing
 * could be restored safely, so they are refetched when the last one settles. */
const stale = new Set<EntityName>();

function optimistic(
  queryClient: QueryClient,
  touches: readonly EntityName[],
  apply: (scope: string, vars: Record<string, unknown>) => void,
) {
  return {
    onMutate: async (vars: Base & Record<string, unknown>) => {
      // Only this user's entity queries; `{ queryKey: [] }` matched every query.
      await queryClient.cancelQueries({
        predicate: (query) => query.queryKey[1] === vars.scope,
      });
      const snapshot = cacheSnapshot(queryClient, vars.scope, touches);
      apply(vars.scope, vars);
      return { snapshot, scope: vars.scope };
    },
    onError: (
      _error: unknown,
      _vars: Base,
      context?: { snapshot: ReturnType<typeof cacheSnapshot>; scope: string },
    ) => {
      if (!context) return;
      if (isLastPending(queryClient)) {
        cacheRestore(queryClient, context.scope, context.snapshot);
      } else {
        touches.forEach((entity) => stale.add(entity));
      }
    },
    onSettled: (_data: unknown, _error: unknown, vars: Base) => {
      if (!isLastPending(queryClient)) return;
      invalidateScope(queryClient, vars.scope, [
        ...new Set([...touches, ...stale]),
      ]);
      stale.clear();
    },
  };
}

async function insert(entity: EntityName, row: Record<string, unknown>) {
  await putRow(entity, row);
}

async function patch(
  entity: EntityName,
  id: string,
  fields: Record<string, unknown>,
) {
  await patchRow(entity, id, fields);
}

export function installMutationDefaults(queryClient: QueryClient) {
  const set = (
    key: readonly [EntityName, string],
    mutationFn: (vars: never) => Promise<unknown>,
    apply: (scope: string, vars: Record<string, unknown>) => void,
    touches: readonly EntityName[],
  ) => {
    queryClient.setMutationDefaults([...key], {
      mutationFn: mutationFn as (vars: unknown) => Promise<unknown>,
      ...optimistic(queryClient, touches, apply),
    });
  };

  // --- lists ---------------------------------------------------------
  set(
    ["lists", "create"],
    async (vars: Base & { row: List }) => insert("lists", vars.row),
    (scope, vars) => cacheUpsert(queryClient, "lists", scope, vars.row as List),
    ["lists"],
  );
  set(
    ["lists", "update"],
    async (vars: Base & { id: string; fields: Partial<List> }) =>
      patch("lists", vars.id, vars.fields),
    (scope, vars) =>
      cachePatch<List>(
        queryClient,
        "lists",
        scope,
        vars.id as string,
        vars.fields as Partial<List>,
      ),
    ["lists"],
  );
  set(
    ["lists", "remove"],
    async (vars: Base & { id: string }) => {
      // The API trashes the list's tasks along with it.
      await unwrap(api.deleteList({ path: { id: vars.id } }));
    },
    (scope, vars) => {
      cacheRemove<Task>(
        queryClient,
        "tasks",
        scope,
        (task) => task.list_id === vars.id,
      );
      cacheRemove<List>(queryClient, "lists", scope, (l) => l.id === vars.id);
    },
    ["lists", "tasks"],
  );

  // --- tasks --------------------------------------------------------
  set(
    ["tasks", "create"],
    async (vars: Base & { row: Task }) => insert("tasks", vars.row),
    (scope, vars) => cacheUpsert(queryClient, "tasks", scope, vars.row as Task),
    ["tasks"],
  );
  set(
    ["tasks", "update"],
    async (vars: Base & { id: string; fields: Partial<Task> }) =>
      patch("tasks", vars.id, vars.fields),
    (scope, vars) =>
      cachePatch<Task>(
        queryClient,
        "tasks",
        scope,
        vars.id as string,
        vars.fields as Partial<Task>,
      ),
    ["tasks"],
  );
  set(
    ["tasks", "remove"],
    async (vars: Base & { id: string }) =>
      patch("tasks", vars.id, { deleted: true }),
    (scope, vars) =>
      cacheRemove<Task>(queryClient, "tasks", scope, (t) => t.id === vars.id),
    ["tasks"],
  );

  // Compound: mark a completion + roll/close the task.
  set(
    ["tasks", "complete"],
    async (
      vars: Base & {
        completion?: TaskCompletion;
        taskId: string;
        taskFields: Partial<Task>;
      },
    ) => {
      if (vars.completion) {
        await insert("task_completions", vars.completion);
      }
      await patch("tasks", vars.taskId, vars.taskFields);
    },
    (scope, vars) => {
      if (vars.completion) {
        cacheUpsert(
          queryClient,
          "task_completions",
          scope,
          vars.completion as TaskCompletion,
        );
      }
      cachePatch<Task>(
        queryClient,
        "tasks",
        scope,
        vars.taskId as string,
        vars.taskFields as Partial<Task>,
      );
    },
    ["tasks", "task_completions"],
  );
  set(
    ["tasks", "uncomplete"],
    async (
      vars: Base & {
        completionIds: string[];
        taskId: string;
        taskFields: Partial<Task>;
      },
    ) => {
      for (const id of vars.completionIds) {
        await patch("task_completions", id, { deleted: true });
      }
      await patch("tasks", vars.taskId, vars.taskFields);
    },
    (scope, vars) => {
      const ids = new Set(vars.completionIds as string[]);
      cacheRemove<TaskCompletion>(
        queryClient,
        "task_completions",
        scope,
        (row) => ids.has(row.id),
      );
      cachePatch<Task>(
        queryClient,
        "tasks",
        scope,
        vars.taskId as string,
        vars.taskFields as Partial<Task>,
      );
    },
    ["tasks", "task_completions"],
  );

  // --- task_alerts -----------------------------------------------
  set(
    ["task_alerts", "create"],
    async (vars: Base & { row: TaskAlert }) => insert("task_alerts", vars.row),
    (scope, vars) =>
      cacheUpsert(queryClient, "task_alerts", scope, vars.row as TaskAlert),
    ["task_alerts"],
  );
  set(
    ["task_alerts", "update"],
    async (vars: Base & { id: string; fields: Partial<TaskAlert> }) =>
      patch("task_alerts", vars.id, vars.fields),
    (scope, vars) =>
      cachePatch<TaskAlert>(
        queryClient,
        "task_alerts",
        scope,
        vars.id as string,
        vars.fields as Partial<TaskAlert>,
      ),
    ["task_alerts"],
  );
}

export type MutationVars = {
  listCreate: Base & { row: List };
  listUpdate: Base & { id: string; fields: Partial<List> };
  listRemove: Base & { id: string };
  taskCreate: Base & { row: Task };
  taskUpdate: Base & { id: string; fields: Partial<Task> };
  taskRemove: Base & { id: string };
  taskComplete: Base & {
    completion?: TaskCompletion;
    taskId: string;
    taskFields: Partial<Task>;
  };
  taskUncomplete: Base & {
    completionIds: string[];
    taskId: string;
    taskFields: Partial<Task>;
  };
  alertCreate: Base & { row: TaskAlert };
  alertUpdate: Base & { id: string; fields: Partial<TaskAlert> };
};

export const mk = {
  listCreate: ["lists", "create"] as const,
  listUpdate: ["lists", "update"] as const,
  listRemove: ["lists", "remove"] as const,
  taskCreate: ["tasks", "create"] as const,
  taskUpdate: ["tasks", "update"] as const,
  taskRemove: ["tasks", "remove"] as const,
  taskComplete: ["tasks", "complete"] as const,
  taskUncomplete: ["tasks", "uncomplete"] as const,
  alertCreate: ["task_alerts", "create"] as const,
  alertUpdate: ["task_alerts", "update"] as const,
};
